// 휴대폰 폭에서는 표 대신 카드 목록으로 보여 주는 DataGrid.
//
// 목록 화면 대부분이 데스크톱용 표라서 폭 412px 에서는 4~10칸 중 1~3칸만 보였다(2026-10-08 모바일 점검:
// 메뉴 38개 중 25개). 화면마다 따로 고치지 않고, `import { DataGrid } from '@mui/x-data-grid'` 를
// 이 파일로 바꾸기만 하면 되도록 같은 props 를 그대로 받는다.
//
// - 넓은 화면: @mui/x-data-grid 의 DataGrid 를 그대로 그린다(동작·모양 변화 없음).
// - 좁은 화면: 행마다 카드 하나. 제목 1줄 + 상태 칩 + 나머지 칸 최대 4개를 "칸 이름: 값"으로.
//   행을 누르면 원래 onRowClick 을 그대로 부른다.
// - 체크박스 선택·셀 편집처럼 표에서만 되는 기능이 있는 화면은 휴대폰에서도 표를 유지한다.
// - apiRef 를 넘기는 화면은 컨테이너가 apiRef.current 를 부를 수 있어서, 카드 뒤에 표를 안 보이게
//   같이 띄워 둔다(엑셀 내보내기 등이 그대로 동작하도록).
//
// 화면에서 카드 모양을 정하고 싶으면 mobileCard={{ title: 'cstNm', subtitle: 'customerNm',
// badge: 'cstStatus', fields: ['expSales', 'expDay'] }} 처럼 칸 이름(field)을 지정한다.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid';
import { Box, Button, CircularProgress, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import CustomToolbar from './CustomToolbar';
import { buildGridKey, applyPref, isUserToggleable, PREF_VERSION } from './gridPref';
import * as gridPrefStore from '../../../lib/gridPrefStore';

const PAGE = 20;
const SKIP_FIELDS = new Set(['__check__', '__detail_panel_toggle__', 'actions', 'id']);
const TITLE_HINT = /(명|제목|이름|건명|사업|프로젝트명|품명|name|title)/i;
const BADGE_HINT = /(상태|단계|진행|구분)/;

const isEmpty = (v) => v === null || v === undefined || v === '';

// v7 시그니처: valueGetter(value, row, column, apiRef), valueFormatter(value, row, column, apiRef)
const rawValue = (col, row, apiRef) => {
  const base = row?.[col.field];
  if (typeof col.valueGetter === 'function') {
    try {
      return col.valueGetter(base, row, col, apiRef);
    } catch {
      return base;
    }
  }
  return base;
};

const formatted = (col, row, value, apiRef) => {
  if (typeof col.valueFormatter === 'function') {
    try {
      return col.valueFormatter(value, row, col, apiRef);
    } catch {
      return value;
    }
  }
  if (typeof value === 'boolean') return value ? '예' : '아니오';
  if (typeof value === 'number') return value.toLocaleString();
  return value;
};

const cellContent = (col, row, id, apiRef) => {
  const value = rawValue(col, row, apiRef);
  const text = formatted(col, row, value, apiRef);
  if (typeof col.renderCell === 'function') {
    try {
      const node = col.renderCell({
        id, row, field: col.field, value, formattedValue: text, colDef: col,
        api: apiRef?.current, rowNode: { id, type: 'leaf' }, hasFocus: false, tabIndex: -1, cellMode: 'view',
      });
      if (node !== null && node !== undefined && node !== false) return node;
    } catch {
      // renderCell 이 표 내부 API 를 기대하면 실패할 수 있다. 그때는 글자로 보여 준다.
    }
  }
  if (isEmpty(text)) return null;
  return typeof text === 'object' && !React.isValidElement(text) ? String(text) : text;
};

const pickLayout = (columns, mobileCard) => {
  const byField = (f) => columns.find((c) => c.field === f);
  if (mobileCard?.title) {
    const title = byField(mobileCard.title);
    const subtitle = mobileCard.subtitle ? byField(mobileCard.subtitle) : null;
    const badge = mobileCard.badge ? byField(mobileCard.badge) : null;
    const used = new Set([title, subtitle, badge].filter(Boolean).map((c) => c.field));
    const fields = mobileCard.fields
      ? mobileCard.fields.map(byField).filter(Boolean)
      : columns.filter((c) => !used.has(c.field)).slice(0, 4);
    return { title, subtitle, badge, fields };
  }
  // 자동 배치: 헤더 이름으로 역할을 짐작한다.
  // - 제목: 사업명·건명·제목·품명처럼 "무엇"을 말하는 칸 > 그 밖의 "~명/이름" 칸 > 가장 넓은 칸
  // - 부제: 고객·거래처·회사 칸(제목이 아닐 때)
  // - 칩: 상태·단계 칸(없으면 구분·유형 칸), 칩 모양을 그리는 renderCell 이 있을 때만
  // - 나머지: 금액·날짜 칸을 앞으로, 최대 4개
  const header = (c) => `${c.headerName || ''}`;
  const width = (c) => (c.flex ? 1000 * c.flex : c.width || c.minWidth || 100);
  const find = (re, except = []) => columns.find((c) => re.test(header(c)) && !except.includes(c));
  const title = find(/(사업명|건명|제목|프로젝트명|품명|제품명|창고명|공정명|작업명|계획명)/)
    || find(TITLE_HINT)
    || [...columns].sort((x, y) => width(y) - width(x))[0];
  const subtitle = find(/(고객|거래처|회사명|업체|고객사)/, [title]) || null;
  const badge = columns.find((c) => c !== title && c !== subtitle && typeof c.renderCell === 'function' && BADGE_HINT.test(header(c)))
    || columns.find((c) => c !== title && c !== subtitle && typeof c.renderCell === 'function' && /(구분|유형|타입|종류)/.test(header(c)))
    || null;
  const rest = columns.filter((c) => c !== title && c !== subtitle && c !== badge);
  // 등록일·수정일 같은 기록용 날짜는 맨 뒤로(거래처 카드에서 사업자번호·연락처를 밀어냈다).
  const rank = (c) => {
    const h = header(c);
    if (/(등록|수정|작성|생성|변경)(일|일시|자)/.test(h)) return 3;
    if (/(금액|매출|매입|합계|단가|수량|잔액)/.test(h)) return 0;
    if (/(일$|일자|날짜|기간|예정)/.test(h)) return 1;
    return 2;
  };
  const fields = [...rest].sort((x, y) => rank(x) - rank(y)).slice(0, 4);
  return { title, subtitle, badge, fields };
};

const MobileCardList = ({ rows, columns, getRowId, onRowClick, loading, apiRef, mobileCard, server }) => {
  const [limit, setLimit] = useState(PAGE);
  const layout = useMemo(() => pickLayout(columns, mobileCard), [columns, mobileCard]);
  const list = rows || [];

  if (loading && list.length === 0) {
    return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  }
  if (list.length === 0) {
    return <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>데이터가 없습니다.</Typography>;
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Stack spacing={1} sx={{ pb: 1 }}>
        {(server ? list : list.slice(0, limit)).map((row, index) => {
          const id = getRowId ? getRowId(row) : (row.id ?? index);
          const title = layout.title ? cellContent(layout.title, row, id, apiRef) : null;
          const subtitle = layout.subtitle ? cellContent(layout.subtitle, row, id, apiRef) : null;
          const badge = layout.badge ? cellContent(layout.badge, row, id, apiRef) : null;
          const clickable = Boolean(onRowClick);
          return (
            <Box
              key={id}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? (event) => onRowClick({ id, row, columns }, event) : undefined}
              sx={{
                p: 1.25,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                cursor: clickable ? 'pointer' : 'default',
                '&:active': clickable ? { bgcolor: 'action.hover' } : undefined,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', wordBreak: 'keep-all', overflowWrap: 'anywhere' }} component="div">
                    {title ?? '-'}
                  </Typography>
                  {subtitle && (
                    <Typography variant="caption" color="text.secondary" component="div" noWrap>
                      {subtitle}
                    </Typography>
                  )}
                </Box>
                {badge && <Box sx={{ flexShrink: 0, maxWidth: '45%', overflow: 'hidden' }}>{badge}</Box>}
              </Stack>
              {layout.fields.length > 0 && (
                <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 1.5, rowGap: 0.25 }}>
                  {layout.fields.map((col) => {
                    const content = cellContent(col, row, id, apiRef);
                    return (
                      <Box key={col.field} sx={{ minWidth: 0, display: 'flex', gap: 0.5, alignItems: 'baseline' }}>
                        {/* 헤더 이름이 없는 칸(편집 아이콘 같은 동작 칸)은 이름표 없이 내용만. 필드명(qteRegDtAction)이 보였다. */}
                        {col.headerName && (
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {col.headerName}
                          </Typography>
                        )}
                        <Typography variant="caption" component="div" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {content ?? '-'}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
      {server && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 1 }}>
          <Button size="small" disabled={server.page <= 0} onClick={() => server.go(server.page - 1)}>이전</Button>
          <Typography variant="caption" color="text.secondary">
            {server.page + 1} / {Math.max(1, Math.ceil(server.total / server.pageSize))} 페이지 · 총 {server.total.toLocaleString()}건
          </Typography>
          <Button size="small" disabled={(server.page + 1) * server.pageSize >= server.total} onClick={() => server.go(server.page + 1)}>다음</Button>
        </Stack>
      )}
      {!server && list.length > limit && (
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Button size="small" onClick={() => setLimit((n) => n + PAGE)}>
            더 보기 ({limit}/{list.length})
          </Button>
        </Box>
      )}
    </Box>
  );
};

const ResponsiveDataGrid = React.forwardRef(function ResponsiveDataGrid(props, ref) {
  const {
    mobileCard, disableMobileCards, gridId, disablePersonalization, toolbar, ...gridProps
  } = props;
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  // 그리드 개인화(열 순서/숨김/너비, 정렬, 밀도, 페이지 크기 — 필터는 제외) 전체를 묶어서 끈다.
  const personalizationOn = !disablePersonalization;

  const gridKey = useMemo(
    () => (personalizationOn ? buildGridKey(location.pathname, gridId, gridProps.columns) : null),
    [personalizationOn, location.pathname, gridId, gridProps.columns],
  );

  const [pref, setPref] = useState(() => (gridKey ? gridPrefStore.getPref(gridKey) || null : null));
  const prefRef = useRef(pref);
  useEffect(() => { prefRef.current = pref; }, [pref]);

  useEffect(() => {
    if (!gridKey) return undefined;
    setPref(gridPrefStore.getPref(gridKey) || null);
    // 로그인 후 처음 그리는 그리드가 전체 설정을 한 번 불러온다(store 가 사용자당 한 번만 실제 요청).
    gridPrefStore.ensureLoaded();
    const unsubscribe = gridPrefStore.subscribe(() => {
      setPref(gridPrefStore.getPref(gridKey) || null);
    });
    return unsubscribe;
  }, [gridKey]);

  const updatePref = (patch) => {
    if (!gridKey) return;
    const next = { v: PREF_VERSION, ...(prefRef.current || {}), ...patch };
    prefRef.current = next;
    setPref(next);
    gridPrefStore.savePref(gridKey, next);
  };

  const handleResetToDefault = () => {
    if (!gridKey) return;
    prefRef.current = null;
    setPref(null);
    gridPrefStore.resetPref(gridKey);
  };

  const handleApplyColumnSettings = ({ order, hidden }) => updatePref({ order, hidden });

  const codeVisibilityModel = gridProps.columnVisibilityModel || {};
  const codeVisibilityKey = JSON.stringify(codeVisibilityModel);

  const { columns: appliedColumns, visibilityModel: appliedVisibilityModel } = useMemo(() => {
    if (!personalizationOn) {
      return { columns: gridProps.columns || [], visibilityModel: codeVisibilityModel };
    }
    return applyPref(gridProps.columns, codeVisibilityModel, pref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridProps.columns, codeVisibilityKey, pref, personalizationOn]);

  // 컨테이너가 이미 그 값을 직접 제어하고 있으면(controlled prop) 맞서지 않는다 — 우리가 떠맡는 건
  // 컨테이너가 안 쓰는 facet 뿐이다. autoPageSize 는 컨테이너 높이로 pageSize 를 스스로 계산하는
  // 기능이라 우리가 고정 paginationModel 로 가로채면 그 계산이 무의미해진다 — 제외한다.
  const weControlSort = personalizationOn && gridProps.sortModel === undefined;
  const weControlDensity = personalizationOn && gridProps.density === undefined;
  const weControlPagination = personalizationOn
    && gridProps.paginationModel === undefined
    && !gridProps.autoPageSize;

  // 많은 화면이 initialState.pagination.paginationModel.pageSize 로 기본 페이지 크기를 정해 둔다
  // (컨트롤드 prop이 아니라 MUI 초기값일 뿐). 우리가 paginationModel 을 가로채도 그 값을 1회성
  // 시드로 그대로 이어받아야 기존 화면 동작이 안 바뀐다. MUI 기본값은 100(autoPageSize 면 0).
  const initialPaginationModel = useMemo(() => ({
    page: gridProps.initialState?.pagination?.paginationModel?.page ?? 0,
    pageSize: gridProps.initialState?.pagination?.paginationModel?.pageSize
      ?? (gridProps.autoPageSize ? 0 : 100),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const [localSort, setLocalSort] = useState([]);
  const [localDensity, setLocalDensity] = useState('standard');
  const [localPaginationModel, setLocalPaginationModel] = useState(initialPaginationModel);
  const appliedInitialRef = useRef(false);

  useEffect(() => {
    // pref 로딩이 늦게(서버 응답 뒤) 끝나도 한 번은 반영한다. 그 뒤로는 사용자가 이미 조작했을 수
    // 있으니 다시 덮어쓰지 않는다.
    if (appliedInitialRef.current || !pref) return;
    appliedInitialRef.current = true;
    if (weControlSort && Array.isArray(pref.sort)) setLocalSort(pref.sort);
    if (weControlDensity && pref.density) setLocalDensity(pref.density);
    if (weControlPagination && pref.pageSize) {
      setLocalPaginationModel((m) => ({ ...m, pageSize: pref.pageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pref]);

  const handleColumnVisibilityModelChange = (model, details) => {
    if (personalizationOn) {
      const colsByField = new Map((gridProps.columns || []).map((c) => [c.field, c]));
      const hiddenFields = Object.keys(model)
        .filter((f) => model[f] === false && isUserToggleable(colsByField.get(f), codeVisibilityModel));
      updatePref({ hidden: hiddenFields });
    }
    gridProps.onColumnVisibilityModelChange?.(model, details);
  };

  const handleColumnWidthChange = (params, event, details) => {
    if (personalizationOn) {
      updatePref({ widths: { ...(prefRef.current?.widths || {}), [params.colDef.field]: params.width } });
    }
    gridProps.onColumnWidthChange?.(params, event, details);
  };

  const handleSortModelChange = (model, details) => {
    if (personalizationOn) {
      if (weControlSort) setLocalSort(model);
      updatePref({ sort: model });
    }
    gridProps.onSortModelChange?.(model, details);
  };

  const handleDensityChange = (density) => {
    if (personalizationOn) {
      if (weControlDensity) setLocalDensity(density);
      updatePref({ density });
    }
    gridProps.onDensityChange?.(density);
  };

  const handlePaginationModelChange = (model, details) => {
    if (personalizationOn) {
      if (weControlPagination) setLocalPaginationModel(model);
      updatePref({ pageSize: model.pageSize });
    }
    gridProps.onPaginationModelChange?.(model, details);
  };

  // 서버 페이징인데 필터가 서버 쪽에서 안 걸리면, 지금 페이지만 걸러지는 필터는 오해를 부른다
  // (사용자는 전체에서 걸러졌다고 생각하지만 실제로는 받은 한 페이지 안에서만 걸러진다).
  const serverFilterBlocked = gridProps.paginationMode === 'server' && gridProps.filterMode !== 'server';

  const existingToolbarSlot = gridProps.slots?.toolbar;
  const ToolbarComponent = existingToolbarSlot || (toolbar === false ? undefined : CustomToolbar);
  const slots = ToolbarComponent ? { ...gridProps.slots, toolbar: ToolbarComponent } : gridProps.slots;
  const slotProps = ToolbarComponent ? {
    ...gridProps.slotProps,
    toolbar: {
      ...gridProps.slotProps?.toolbar,
      gridPersonalization: personalizationOn ? {
        columns: appliedColumns,
        codeVisibilityModel,
        hidden: pref?.hidden || [],
        onApply: handleApplyColumnSettings,
        onReset: handleResetToDefault,
        filterHidden: serverFilterBlocked,
      } : undefined,
    },
  } : gridProps.slotProps;

  const mergedGridProps = {
    ...gridProps,
    columns: appliedColumns,
    columnVisibilityModel: appliedVisibilityModel,
    onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
    onColumnWidthChange: handleColumnWidthChange,
    onSortModelChange: handleSortModelChange,
    onDensityChange: handleDensityChange,
    onPaginationModelChange: handlePaginationModelChange,
    slots,
    slotProps,
    ...(weControlSort ? { sortModel: localSort } : {}),
    ...(weControlDensity ? { density: localDensity } : {}),
    ...(weControlPagination ? { paginationModel: localPaginationModel } : {}),
  };

  const tableOnly = disableMobileCards
    || gridProps.checkboxSelection
    // 셀 편집은 저장 처리(processRowUpdate)가 붙어 있을 때만 실제 기능이다. 예전 화면에 editable: true 와
    // onRowEditStop(기본 동작만 막는 틀)만 남아 있고 저장은 안 하는 표가 많아서, 그런 표는 카드로 바꿔도 잃는 게 없다.
    || typeof gridProps.processRowUpdate === 'function'
    // 칸마다 다른 동작을 하는 화면(onCellClick)은 카드 한 장으로 합치면 동작을 잃는다.
    || (typeof gridProps.onCellClick === 'function' && typeof gridProps.onRowClick !== 'function');

  if (!isNarrow || tableOnly) {
    return <MuiDataGrid ref={ref} {...mergedGridProps} />;
  }

  // 서버 페이징 화면: 받은 한 페이지를 카드로 보여 주고, 이전/다음은 원래 onPaginationModelChange 로 넘긴다.
  const server = mergedGridProps.paginationMode === 'server'
    ? {
      page: mergedGridProps.paginationModel?.page ?? 0,
      pageSize: mergedGridProps.paginationModel?.pageSize ?? 20,
      total: Number(mergedGridProps.rowCount) || 0,
      go: (page) => mergedGridProps.onPaginationModelChange?.({ page, pageSize: mergedGridProps.paginationModel?.pageSize ?? 20 }, {}),
    }
    : null;
  // 모바일 카드도 개인화된 순서·숨김을 그대로 따른다(appliedColumns/appliedVisibilityModel 이미 반영됨).
  const hidden = mergedGridProps.columnVisibilityModel || {};
  const columns = (mergedGridProps.columns || []).filter(
    (c) => !SKIP_FIELDS.has(c.field) && c.type !== 'actions' && hidden[c.field] !== false,
  );

  return (
    <>
      <MobileCardList
        rows={mergedGridProps.rows}
        columns={columns}
        getRowId={mergedGridProps.getRowId}
        onRowClick={mergedGridProps.onRowClick}
        loading={mergedGridProps.loading}
        apiRef={mergedGridProps.apiRef}
        mobileCard={mobileCard}
        server={server}
      />
      {mergedGridProps.apiRef && (
        <Box aria-hidden sx={{ position: 'absolute', left: -10000, top: 0, width: 800, height: 400, visibility: 'hidden' }}>
          <MuiDataGrid ref={ref} {...mergedGridProps} />
        </Box>
      )}
    </>
  );
});

export default ResponsiveDataGrid;
export { ResponsiveDataGrid as DataGrid };
